<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="860px"
    persistent
    content-class="dni-capture-dialog"
  >
    <v-card class="rounded-xl overflow-hidden elevation-0 border-light" outlined>
      <!-- Cabecera Minimalista Limpia (Fondo Neutro Plano SunExpert) -->
      <div class="px-5 py-4 d-flex align-center justify-space-between bg-white border-bottom">
        <div class="d-flex align-center">
          <v-avatar size="38" color="grey lighten-4" class="mr-3">
            <v-icon color="grey darken-3" size="20">mdi-card-account-details-outline</v-icon>
          </v-avatar>
          <div>
            <div class="text-subtitle-1 font-weight-bold grey--text text--darken-4">
              Digitalización de DNI
            </div>
            <div class="text-caption grey--text text--darken-2">
              Captura fotográfica y generación de copia anexa al expediente digital
            </div>
          </div>
        </div>
        <v-btn icon small color="grey darken-2" @click="closeModal" :disabled="saving">
          <v-icon size="18">mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="pa-4 pa-sm-5" style="background-color: #f8fafc;">
        <!-- Banner del Trabajador Elegante (Neutro, sin saturaciones) -->
        <v-card
          elevation="0"
          outlined
          class="pa-3 mb-4 rounded-xl bg-white border-light d-flex flex-wrap align-center justify-space-between"
        >
          <div class="d-flex align-center">
            <v-avatar color="grey lighten-4" size="40" class="mr-3">
              <v-icon color="grey darken-3" size="22">mdi-account-outline</v-icon>
            </v-avatar>
            <div>
              <div class="text-subtitle-2 font-weight-bold grey--text text--darken-4">
                {{ workerName || "Colaborador(a)" }}
              </div>
              <div class="text-caption grey--text text--darken-2">
                <span class="font-weight-medium">DNI:</span> {{ dni || "No especificado" }}
                <span class="mx-1 grey--text text--lighten-1">•</span>
                <span class="font-weight-medium">Puesto:</span> {{ positionName || "General" }}
              </div>
            </div>
          </div>
          <div class="text-right mt-2 mt-sm-0">
            <v-chip x-small color="grey lighten-3" class="font-weight-medium grey--text text--darken-3">
              <v-icon x-small left color="grey darken-2">mdi-clock-outline</v-icon>
              {{ currentDateTimeFormatted }}
            </v-chip>
          </div>
        </v-card>

        <!-- Cuadrícula de Captura (2 Caras: Anverso y Reverso) -->
        <v-row dense>
          <!-- CARA 1: ANVERSO -->
          <v-col cols="12" md="6">
            <v-card
              outlined
              elevation="0"
              class="pa-3 rounded-xl bg-white fill-height d-flex flex-column dni-card-box transition-all"
              :class="{
                'border-active': activeTarget === 'front',
                'border-drag-over': dragTarget === 'front'
              }"
              @dragover.prevent="onDragOver('front')"
              @dragleave.prevent="onDragLeave('front')"
              @drop.prevent="onDrop($event, 'front')"
            >
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="d-flex align-center">
                  <v-icon small color="grey darken-3" class="mr-1">mdi-numeric-1-circle-outline</v-icon>
                  <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4">
                    Cara Frontal (Anverso)
                  </span>
                </div>
                <v-chip
                  x-small
                  :color="frontImage ? 'success' : 'grey lighten-3'"
                  :text-color="frontImage ? 'white' : 'grey darken-3'"
                  class="font-weight-bold"
                >
                  <v-icon x-small left v-if="frontImage">mdi-check</v-icon>
                  {{ frontImage ? "Capturado" : "Pendiente" }}
                </v-chip>
              </div>

              <!-- Vista previa, cámara activa o drag drop -->
              <div
                class="dni-preview-box flex-grow-1 d-flex flex-column align-center justify-center rounded-lg border-light mb-3 position-relative"
                style="min-height: 210px; max-height: 240px; overflow: hidden; background: #fafafa;"
              >
                <!-- Si se está usando la cámara para el anverso -->
                <div
                  v-if="isCameraActive && activeTarget === 'front'"
                  class="w-100 h-100 position-relative d-flex align-center justify-center"
                >
                  <video
                    ref="videoFront"
                    autoplay
                    playsinline
                    class="dni-video"
                  ></video>
                  <div class="dni-guide-frame">
                    <div class="dni-scan-line"></div>
                    <span>Encuadre el frente del DNI</span>
                  </div>
                </div>

                <!-- Si ya se capturó la foto -->
                <v-img
                  v-else-if="frontImage"
                  :src="frontImage"
                  max-height="220"
                  contain
                  class="rounded-lg"
                />

                <!-- Estado vacío / drop zone -->
                <div v-else class="text-center pa-4 cursor-pointer" @click="triggerFileSelect('front')">
                  <v-avatar size="50" color="grey lighten-3" class="mb-2">
                    <v-icon size="26" color="grey darken-2">mdi-camera-outline</v-icon>
                  </v-avatar>
                  <div class="text-caption font-weight-bold grey--text text--darken-3">
                    {{ dragTarget === 'front' ? '¡Suelta la imagen aquí!' : 'Captura con cámara o arrastra una foto' }}
                  </div>
                  <div class="text-caption grey--text text--darken-1" style="font-size: 11px;">
                    Haz clic aquí para seleccionar archivo
                  </div>
                </div>
              </div>

              <!-- Botonera de acciones Anverso -->
              <div class="d-flex align-center gap-2">
                <v-btn
                  v-if="isCameraActive && activeTarget === 'front'"
                  color="primary"
                  block
                  elevation="0"
                  class="rounded-lg font-weight-bold"
                  @click="snapPhoto('front')"
                >
                  <v-icon left small>mdi-camera-iris</v-icon> Disparar Foto
                </v-btn>
                <template v-else>
                  <v-btn
                    color="primary"
                    small
                    outlined
                    class="flex-grow-1 rounded-lg font-weight-bold"
                    @click="startCamera('front')"
                  >
                    <v-icon left small>mdi-camera</v-icon> Cámara
                  </v-btn>
                  <v-btn
                    color="grey darken-2"
                    small
                    outlined
                    class="flex-grow-1 rounded-lg font-weight-bold"
                    @click="triggerFileSelect('front')"
                  >
                    <v-icon left small>mdi-upload</v-icon> Subir
                  </v-btn>
                  <v-tooltip bottom v-if="frontImage">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        color="error"
                        @click="frontImage = null"
                      >
                        <v-icon small>mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Eliminar imagen frontal</span>
                  </v-tooltip>
                </template>
              </div>
            </v-card>
          </v-col>

          <!-- CARA 2: REVERSO -->
          <v-col cols="12" md="6">
            <v-card
              outlined
              elevation="0"
              class="pa-3 rounded-xl bg-white fill-height d-flex flex-column dni-card-box transition-all"
              :class="{
                'border-active': activeTarget === 'back',
                'border-drag-over': dragTarget === 'back'
              }"
              @dragover.prevent="onDragOver('back')"
              @dragleave.prevent="onDragLeave('back')"
              @drop.prevent="onDrop($event, 'back')"
            >
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="d-flex align-center">
                  <v-icon small color="grey darken-3" class="mr-1">mdi-numeric-2-circle-outline</v-icon>
                  <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4">
                    Cara Posterior (Reverso)
                  </span>
                </div>
                <v-chip
                  x-small
                  :color="backImage ? 'success' : 'grey lighten-3'"
                  :text-color="backImage ? 'white' : 'grey darken-3'"
                  class="font-weight-bold"
                >
                  <v-icon x-small left v-if="backImage">mdi-check</v-icon>
                  {{ backImage ? "Capturado" : "Pendiente" }}
                </v-chip>
              </div>

              <!-- Vista previa, cámara activa o drag drop -->
              <div
                class="dni-preview-box flex-grow-1 d-flex flex-column align-center justify-center rounded-lg border-light mb-3 position-relative"
                style="min-height: 210px; max-height: 240px; overflow: hidden; background: #fafafa;"
              >
                <!-- Si se está usando la cámara para el reverso -->
                <div
                  v-if="isCameraActive && activeTarget === 'back'"
                  class="w-100 h-100 position-relative d-flex align-center justify-center"
                >
                  <video
                    ref="videoBack"
                    autoplay
                    playsinline
                    class="dni-video"
                  ></video>
                  <div class="dni-guide-frame">
                    <div class="dni-scan-line"></div>
                    <span>Encuadre el reverso del DNI</span>
                  </div>
                </div>

                <!-- Si ya se capturó la foto -->
                <v-img
                  v-else-if="backImage"
                  :src="backImage"
                  max-height="220"
                  contain
                  class="rounded-lg"
                />

                <!-- Estado vacío / drop zone -->
                <div v-else class="text-center pa-4 cursor-pointer" @click="triggerFileSelect('back')">
                  <v-avatar size="50" color="grey lighten-3" class="mb-2">
                    <v-icon size="26" color="grey darken-2">mdi-card-text-outline</v-icon>
                  </v-avatar>
                  <div class="text-caption font-weight-bold grey--text text--darken-3">
                    {{ dragTarget === 'back' ? '¡Suelta la imagen aquí!' : 'Captura con cámara o arrastra una foto' }}
                  </div>
                  <div class="text-caption grey--text text--darken-1" style="font-size: 11px;">
                    Haz clic aquí para seleccionar archivo
                  </div>
                </div>
              </div>

              <!-- Botonera de acciones Reverso -->
              <div class="d-flex align-center gap-2">
                <v-btn
                  v-if="isCameraActive && activeTarget === 'back'"
                  color="primary"
                  block
                  elevation="0"
                  class="rounded-lg font-weight-bold"
                  @click="snapPhoto('back')"
                >
                  <v-icon left small>mdi-camera-iris</v-icon> Disparar Foto
                </v-btn>
                <template v-else>
                  <v-btn
                    color="primary"
                    small
                    outlined
                    class="flex-grow-1 rounded-lg font-weight-bold"
                    @click="startCamera('back')"
                  >
                    <v-icon left small>mdi-camera</v-icon> Cámara
                  </v-btn>
                  <v-btn
                    color="grey darken-2"
                    small
                    outlined
                    class="flex-grow-1 rounded-lg font-weight-bold"
                    @click="triggerFileSelect('back')"
                  >
                    <v-icon left small>mdi-upload</v-icon> Subir
                  </v-btn>
                  <v-tooltip bottom v-if="backImage">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        color="error"
                        @click="backImage = null"
                      >
                        <v-icon small>mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Eliminar imagen posterior</span>
                  </v-tooltip>
                </template>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Selector de Cámara si hay múltiples dispositivos -->
        <div v-if="videoDevices.length > 1 && isCameraActive" class="mt-3">
          <v-select
            v-model="selectedCameraId"
            :items="videoDevices"
            item-text="label"
            item-value="deviceId"
            label="Dispositivo de Cámara Seleccionado"
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-webcam"
            class="rounded-lg"
            @change="restartCameraWithDevice"
          />
        </div>

        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          style="display: none;"
          @change="handleFileUpload"
        />
        <canvas ref="snapCanvas" style="display: none;"></canvas>
      </v-card-text>

      <!-- Barra de Acciones Minimalista (Botón Principal Primary) -->
      <v-card-actions class="px-5 py-3 bg-white border-top d-flex justify-space-between">
        <v-btn
          text
          color="grey darken-2"
          class="rounded-lg font-weight-medium"
          @click="closeModal"
          :disabled="saving"
        >
          Cancelar
        </v-btn>

        <div class="d-flex align-center">
          <v-btn
            color="primary"
            elevation="0"
            class="rounded-lg font-weight-bold px-6"
            :loading="saving"
            :disabled="!frontImage && !backImage"
            @click="saveAndGeneratePdf"
          >
            <v-icon left small>mdi-file-pdf-box</v-icon>
            Generar y Anexar PDF al File
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ContractSignService from "@/services/HumanResource/ContractSignService.js";
import swal from "sweetalert2";

export default {
  name: "DniPhotoCaptureModal",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    dni: {
      type: String,
      default: "",
    },
    workerName: {
      type: String,
      default: "",
    },
    positionName: {
      type: String,
      default: "",
    },
    agtId: {
      type: Number,
      default: 0,
    },
    operatorName: {
      type: String,
      default: "",
    },
    initialFrontImage: {
      type: String,
      default: null,
    },
    initialBackImage: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      frontImage: null,
      backImage: null,
      activeTarget: null, // 'front' | 'back'
      dragTarget: null, // 'front' | 'back' | null
      isCameraActive: false,
      videoStream: null,
      videoDevices: [],
      selectedCameraId: null,
      saving: false,
      currentTime: new Date(),
      clockTimer: null,
    };
  },

  computed: {
    currentDateTimeFormatted() {
      const d = this.currentTime;
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.frontImage = this.initialFrontImage || null;
        this.backImage = this.initialBackImage || null;
        this.startClock();
        this.loadVideoDevices();
      } else {
        this.stopCamera();
        this.stopClock();
      }
    },
  },

  beforeDestroy() {
    this.stopCamera();
    this.stopClock();
  },

  methods: {
    startClock() {
      this.stopClock();
      this.currentTime = new Date();
      this.clockTimer = setInterval(() => {
        this.currentTime = new Date();
      }, 1000);
    },

    stopClock() {
      if (this.clockTimer) {
        clearInterval(this.clockTimer);
        this.clockTimer = null;
      }
    },

    async loadVideoDevices() {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return;
        const devices = await navigator.mediaDevices.enumerateDevices();
        this.videoDevices = devices
          .filter((d) => d.kind === "videoinput")
          .map((d, idx) => ({
            label: d.label || `Cámara ${idx + 1}`,
            deviceId: d.deviceId,
          }));
        if (this.videoDevices.length > 0 && !this.selectedCameraId) {
          this.selectedCameraId = this.videoDevices[0].deviceId;
        }
      } catch (err) {
        console.warn("No se pudieron listar dispositivos de video:", err);
      }
    },

    async startCamera(target) {
      this.stopCamera();
      this.activeTarget = target;
      this.isCameraActive = true;

      try {
        const constraints = {
          video: {
            deviceId: this.selectedCameraId ? { exact: this.selectedCameraId } : undefined,
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        this.videoStream = stream;

        this.$nextTick(() => {
          const videoElem = target === "front" ? this.$refs.videoFront : this.$refs.videoBack;
          if (videoElem) {
            videoElem.srcObject = stream;
          }
        });
      } catch (err) {
        this.isCameraActive = false;
        swal.fire({
          icon: "error",
          title: "Error de Cámara",
          text: "No se pudo acceder a la cámara. Por favor verifique los permisos del navegador o use la opción 'Subir'.",
        });
      }
    },

    stopCamera() {
      if (this.videoStream) {
        this.videoStream.getTracks().forEach((track) => track.stop());
        this.videoStream = null;
      }
      this.isCameraActive = false;
      this.activeTarget = null;
    },

    restartCameraWithDevice() {
      if (this.isCameraActive && this.activeTarget) {
        this.startCamera(this.activeTarget);
      }
    },

    snapPhoto(target) {
      const videoElem = target === "front" ? this.$refs.videoFront : this.$refs.videoBack;
      if (!videoElem || !videoElem.videoWidth) return;

      const canvas = this.$refs.snapCanvas;
      canvas.width = videoElem.videoWidth;
      canvas.height = videoElem.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(videoElem, 0, 0, canvas.width, canvas.height);

      const base64 = canvas.toDataURL("image/jpeg", 0.9);
      if (target === "front") {
        this.frontImage = base64;
      } else {
        this.backImage = base64;
      }

      this.stopCamera();
    },

    triggerFileSelect(target) {
      this.stopCamera();
      this.activeTarget = target;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
        this.$refs.fileInput.click();
      }
    },

    onDragOver(target) {
      this.dragTarget = target;
    },

    onDragLeave(target) {
      if (this.dragTarget === target) {
        this.dragTarget = null;
      }
    },

    onDrop(event, target) {
      this.dragTarget = null;
      const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
      if (!file || !file.type || !file.type.startsWith("image/")) return;

      this.stopCamera();
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        if (target === "front") {
          this.frontImage = base64;
        } else {
          this.backImage = base64;
        }
      };
      reader.readAsDataURL(file);
    },

    handleFileUpload(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        if (this.activeTarget === "front") {
          this.frontImage = base64;
        } else if (this.activeTarget === "back") {
          this.backImage = base64;
        }
      };
      reader.readAsDataURL(file);
    },

    async saveAndGeneratePdf() {
      if (!this.frontImage && !this.backImage) {
        swal.fire({
          icon: "warning",
          title: "Imágenes requeridas",
          text: "Debe capturar al menos una de las caras del DNI (frontal o posterior).",
        });
        return;
      }

      const cleanDni = (
        this.dni ||
        (this.$parent &&
          this.$parent.selectedTurn &&
          (this.$parent.selectedTurn.Dni || this.$parent.selectedTurn.dni)) ||
        (this.$parent &&
          this.$parent.contractDetails &&
          (this.$parent.contractDetails.PrsDocumentNumber ||
            this.$parent.contractDetails.Dni ||
            this.$parent.contractDetails.dni)) ||
        ""
      ).toString().trim();

      if (!cleanDni) {
        swal.fire({
          icon: "warning",
          title: "DNI no detectado",
          text: "El número de DNI del colaborador no se encuentra definido en este turno.",
        });
        return;
      }

      this.saving = true;
      try {
        const payload = {
          AgtID: this.agtId || 0,
          Dni: cleanDni,
          WorkerName: this.workerName || "",
          PositionName: this.positionName || "",
          DniFrontBase64: this.frontImage,
          DniBackBase64: this.backImage,
          OperatorName: this.operatorName || (this.$fun && this.$fun.getUserName && this.$fun.getUserName()) || "OPERADOR RRHH",
          DigitizedAt: new Date(),
        };

        const response = await ContractSignService.generateDniCopy(payload, 1);

        if (response && (response.status === 200 || response.data?.success)) {
          swal.fire({
            icon: "success",
            title: "DNI Digitalizado",
            text: "El documento PDF con ambas caras del DNI ha sido generado y archivado en el file del trabajador.",
            timer: 2500,
            showConfirmButton: false,
          });

          this.$emit("saved", {
            frontImage: this.frontImage,
            backImage: this.backImage,
            fileName: response.data?.fileName,
            digitizedAt: new Date(),
          });

          this.closeModal();
        } else {
          throw new Error(response?.data?.message || "No se pudo guardar la copia del DNI.");
        }
      } catch (err) {
        swal.fire({
          icon: "error",
          title: "Error al generar PDF",
          text: err.response?.data?.Message || err.message || "Ocurrió un problema al procesar la digitalización del DNI.",
        });
      } finally {
        this.saving = false;
      }
    },

    closeModal() {
      this.stopCamera();
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped>
.gap-1 {
  gap: 4px;
}
.gap-2 {
  gap: 8px;
}

.border-bottom {
  border-bottom: 1px solid #eef2f6 !important;
}

.border-light {
  border: 1px solid #e2e8f0 !important;
}

.border-active {
  border: 2px solid #1976d2 !important;
}

.border-drag-over {
  border: 2px dashed #1976d2 !important;
  background-color: #f0f7ff !important;
}

.dni-card-box {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.dni-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.dni-guide-frame {
  position: absolute;
  top: 10%;
  left: 6%;
  right: 6%;
  bottom: 10%;
  border: 2px dashed #00e676;
  border-radius: 10px;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 8px;
  overflow: hidden;
}

.dni-scan-line {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, rgba(0, 230, 118, 0) 0%, rgba(0, 230, 118, 1) 50%, rgba(0, 230, 118, 0) 100%);
  box-shadow: 0 0 8px #00e676;
  animation: scanLine 2.5s infinite linear;
}

@keyframes scanLine {
  0% {
    top: 0%;
    opacity: 0;
  }
  15% {
    opacity: 1;
  }
  85% {
    opacity: 1;
  }
  100% {
    top: 98%;
    opacity: 0;
  }
}

.dni-guide-frame span {
  background: rgba(15, 23, 42, 0.75);
  color: #fff;
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 20px;
  letter-spacing: 0.2px;
  font-weight: 500;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
